import { authApi } from '@oneview/api/authApi';
import { documentApi } from '@oneview/api/documentApi';
import { MfaLoginStage, MfaSetupStage } from '@oneview/constants/lookups';
import { setCurrentClientAccount } from '@oneview/redux/actions/accountActions';
import { isAdvisor } from '@oneview/utils';
import { UmaOption, UmaUserSerializer } from '@orbis-systems/orbis-api-clients/uma';
import { Account, User } from '@orbis-systems/orbis-api-middleware';
import { createReducer } from '@reduxjs/toolkit';
import { PersistConfig, persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage/session';
import { setBackgroundPreference } from '../settings';
import {
    loginTimeout,
    setMfaSetupStage,
    setMfaLoginStage,
    setAvailableMfaOptions,
    setEmailVerificationNeeded,
    setPhoneVerificationNeeded,
    setTermsOfUseSigned,
    setUmaUser,
    updateUmaUserMfaEnabled,
} from './authActions';

const DEFAULT_STATE: {
    isLoggedIn: boolean;
    isLoggingIn: boolean;
    isLoginFailed: boolean;
    isLoginTimeout: boolean;
    user: User | null;
    umaUser: UmaUserSerializer | null;
    termsOfUseSigned: boolean;
    currentClientAccount: Account | null;
    backgroundPreference: string;
    mfaSetupStage: MfaSetupStage;
    mfaLoginStage: MfaLoginStage;
    availableMfaOptions: UmaOption[];
    emailVerificationNeeded: boolean;
    phoneVerificationNeeded: boolean;
} = {
    isLoggedIn: false,
    isLoggingIn: false,
    isLoginFailed: false,
    isLoginTimeout: false,
    user: null,
    umaUser: null,
    termsOfUseSigned: false,
    currentClientAccount: null,
    backgroundPreference: ' ',
    mfaSetupStage: MfaSetupStage.NeedInitiation,
    mfaLoginStage: MfaLoginStage.Complete,
    availableMfaOptions: [],
    emailVerificationNeeded: false,
    phoneVerificationNeeded: false,
};

const authReducer = createReducer(DEFAULT_STATE, (builder) => {
    builder.addCase(loginTimeout, (state) => {
        state.isLoginTimeout = true;
    });

    builder.addCase(setCurrentClientAccount, (state, action) => {
        state.currentClientAccount = action.payload;
    });

    builder.addCase(setBackgroundPreference, (state, action) => {
        state.backgroundPreference = action.payload;
    });

    builder.addCase(setTermsOfUseSigned, (state, action) => {
        state.termsOfUseSigned = action.payload;
    });

    builder.addCase(setMfaLoginStage, (state, { payload }) => {
        state.mfaLoginStage = payload;
    });

    builder.addCase(setMfaSetupStage, (state, { payload }) => {
        state.mfaSetupStage = payload;
    });

    builder.addCase(setAvailableMfaOptions, (state, { payload }) => {
        state.availableMfaOptions = payload;
    });

    builder.addCase(setEmailVerificationNeeded, (state, { payload }) => {
        state.emailVerificationNeeded = payload;
    });

    builder.addCase(setPhoneVerificationNeeded, (state, { payload }) => {
        state.phoneVerificationNeeded = payload;
    });

    builder.addCase(setUmaUser, (state, { payload }) => {
        state.umaUser = payload;
    });

    builder.addCase(updateUmaUserMfaEnabled, (state, { payload }) => {
        if (state.umaUser) {
            state.umaUser.mfa_enabled = payload;
        }
    });

    builder.addMatcher(documentApi.endpoints.signLegalDocument.matchRejected, (state) => {
        state.termsOfUseSigned = false;
    });

    builder.addMatcher(documentApi.endpoints.signLegalDocument.matchFulfilled, (state) => {
        state.termsOfUseSigned = true;
    });

    builder.addMatcher(authApi.endpoints.login.matchPending, (state) => {
        state.isLoggedIn = false;
        state.isLoggingIn = true;
    });

    builder.addMatcher(authApi.endpoints.login.matchFulfilled, (state) => {
        state.isLoggedIn = true;
        state.isLoggingIn = false;
        state.isLoginFailed = false;
        state.isLoginTimeout = false;
    });

    builder.addMatcher(authApi.endpoints.login.matchRejected, (state) => {
        state.isLoggedIn = false;
        state.isLoggingIn = false;
        state.isLoginFailed = true;
    });

    builder.addMatcher(authApi.endpoints.logout.matchFulfilled, () => DEFAULT_STATE);

    builder.addMatcher(authApi.endpoints.getUserInfo.matchFulfilled, (state, { payload }) => {
        state.user = payload.user;
    });

    builder.addMatcher(authApi.endpoints.getUmaUserInfo.matchFulfilled, (state, { payload }) => {
        // Bug on UMA side, watchmen_user_links is not returned in the response
        // so we need to merge the existing watchmen_user_links with the new ones
        // TODO: Remove this once UMA fixes the issue
        state.umaUser = { ...payload, watchmen_user_links: state.umaUser?.watchmen_user_links || [] };
    });

    builder.addMatcher(authApi.endpoints.umaLogin.matchFulfilled, (state, { payload }) => {
        state.umaUser = payload.login_basic.user as unknown as UmaUserSerializer;
        if (isAdvisor()) {
            state.emailVerificationNeeded = !payload.login_basic.user.email_verified;
        } else {
            state.emailVerificationNeeded =
                'email_verification_needed' in payload ? (payload.email_verification_needed as boolean) : false;
            state.phoneVerificationNeeded =
                'phone_verification_needed' in payload ? (payload.phone_verification_needed as boolean) : false;
        }
    });

    builder.addMatcher(authApi.endpoints.umaLogin.matchRejected, (state) => {
        state.umaUser = null;
    });
});

const persistConfig: PersistConfig<typeof DEFAULT_STATE> = {
    key: 'auth',
    storage,
    blacklist: [
        'isLoggingIn',
        'isLoginFailed',
        'isPasswordChanged',
        'mfaSetupStage',
        'mfaLoginStage',
        'availableMfaOptions',
    ],
};

const persistAuthReducer = persistReducer(persistConfig, authReducer);

export { persistAuthReducer as authReducer };
